import React from 'react';
import PropTypes from 'prop-types';
import { ComplianceCard } from '../shared';
import StatusBadge from '../shared/StatusBadge';
import './ComplianceStatusGrid.css';

/**
 * ComplianceStatusGrid Component
 * Displays grid of compliance status cards for all compliance areas
 */
const ComplianceStatusGrid = ({ dashboardData, onCardClick }) => {
  if (!dashboardData) return null;

  // Check if profile is approved
  const isProfileApproved = dashboardData.business_profile?.status &&
    ['approved', 'twilio-approved', 'verified'].includes(dashboardData.business_profile.status);

  const businessProfileDetails = dashboardData.business_profile?.exists
    ? [
        {
          label: 'Profile Name',
          value: dashboardData.business_profile.friendly_name || 'N/A',
        },
        {
          label: 'Last Updated',
          value: dashboardData.business_profile.last_updated || 'N/A',
        },
      ]
    : [];

  // Voice compliance details - STIR/SHAKEN always on top
  const voiceComplianceDetails = [
    {
      label: 'SHAKEN/STIR Attestation',
      value: <StatusBadge status={dashboardData.voice_compliance?.shaken_stir?.status || 'not-configured'} size="small" />,
    },
    {
      label: 'CNAM',
      value: <StatusBadge status={dashboardData.voice_compliance?.cnam?.status || 'not-configured'} size="small" />,
    },
    {
      label: 'Voice Integrity',
      value: <StatusBadge status={dashboardData.voice_compliance?.voice_integrity?.status || 'not-configured'} size="small" />,
    },
  ];

  const smsComplianceDetails = [
    {
      label: 'Brand Status',
      value: <StatusBadge status={dashboardData.sms_compliance?.brand?.status || 'not-registered'} size="small" />,
    },
    {
      label: 'Campaign Status',
      value: <StatusBadge status={dashboardData.sms_compliance?.campaign?.status || 'not-registered'} size="small" />,
    },
    {
      label: 'Phones Assigned',
      value: dashboardData.sms_compliance?.phones_assigned || 0,
    },
  ];

  return (
    <div className="compliance-status-grid">
      <ComplianceCard
        title="Business Profile"
        description="Business Profile Verification"
        status={dashboardData.business_profile?.status || 'not_created'}
        details={businessProfileDetails}
        actions={
          dashboardData.business_profile?.exists
            ? [
                {
                  label: 'View Details',
                  onClick: () => onCardClick('profile'),
                  variant: 'secondary',
                },
              ]
            : [
                {
                  label: 'Create Profile',
                  onClick: () => onCardClick('profile'),
                  variant: 'primary',
                },
              ]
        }
      />

      <ComplianceCard
        title="Voice Compliance"
        description="CNAM, SHAKEN/STIR, Voice Integrity"
        status={
          dashboardData.voice_compliance?.cnam?.status === 'twilio-approved' &&
          dashboardData.voice_compliance?.shaken_stir?.status === 'twilio-approved'
            ? 'twilio-approved'
            : 'not-configured'
        }
        details={voiceComplianceDetails}
        actions={[
          {
            label: 'Manage Voice',
            onClick: () => onCardClick('voice'),
            variant: 'secondary',
            disabled: !isProfileApproved,
          },
        ]}
      />

      <ComplianceCard
        title="SMS Compliance"
        description="Brand and Campaign registration"
        status={dashboardData.sms_compliance?.brand?.status || 'not_registered'}
        details={smsComplianceDetails}
        actions={[
          {
            label: 'Manage SMS',
            onClick: () => onCardClick('sms'),
            variant: 'secondary',
            disabled: !isProfileApproved,
          },
        ]}
      />
    </div>
  );
};

ComplianceStatusGrid.propTypes = {
  dashboardData: PropTypes.object.isRequired,
  onCardClick: PropTypes.func.isRequired,
};

export default ComplianceStatusGrid;
